if (!customElements.get('compatibility-badge')) {
  const CHANGE_EVENT = 'compatibility-finder:change';
  const savedEntries = new Map();

  function readEntries(key) {
    if (savedEntries.has(key)) return savedEntries.get(key);

    let entries = [];
    try {
      const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
      if (Array.isArray(parsed)) {
        entries = parsed.filter(entry => entry && Array.isArray(entry.filters) && entry.filters.length);
      }
    } catch (error) {
      // Storage may be unavailable (private browsing).
    }

    savedEntries.set(key, entries);
    return entries;
  }

  /* one listener for the whole page rather than one per badge, so a collection
     of cards costs nothing extra and badges added later are picked up too */
  function refreshAll(key) {
    if (key) {
      savedEntries.delete(key);
    } else {
      savedEntries.clear();
    }

    document.querySelectorAll('compatibility-badge').forEach(badge => badge.render());
  }

  document.addEventListener(CHANGE_EVENT, event => refreshAll(event.detail?.storageKey));
  window.addEventListener('storage', event => {
    if (event.key === null || savedEntries.has(event.key)) refreshAll(event.key);
  });

  class CompatibilityBadge extends HTMLElement {
    connectedCallback() {
      if (window.sectionInstances.has(this)) return;
      window.sectionInstances.set(this, true);

      this.storageKey = this.dataset.storageKey || 'theme-compatibility-finder';

      try {
        this.values = JSON.parse(this.dataset.values || '{}');
      } catch (error) {
        this.values = null;
      }

      this.messages = this.querySelectorAll('.js-compat-confirmed, .js-compat-uncertain');

      /* keep the untouched text so the names can be swapped again when the
         customer renames, adds or removes a saved product */
      this.nameSlots = [];
      this.messages.forEach(message => {
        const walker = document.createTreeWalker(message, NodeFilter.SHOW_TEXT);

        while (walker.nextNode()) {
          const node = walker.currentNode;
          if (node.nodeValue.includes('[name]')) {
            this.nameSlots.push({ node, template: node.nodeValue });
          }
        }
      });

      this.render();
    }

    render() {
      const entries = readEntries(this.storageKey);
      const matched = this.values ? entries.filter(entry => this.fits(entry, this.values)) : [];
      const names = matched.map(entry => entry.name).filter(Boolean).join(', ');

      /* the theme editor previews a state directly. A genuine match still fills
         in its names, otherwise [name] is left in place for the merchant */
      const preview = this.dataset.preview;
      if (preview === 'uncertain') return this.show('.js-compat-uncertain');
      if (preview) return this.show('.js-compat-confirmed', names);

      if (matched.length) return this.show('.js-compat-confirmed', names);

      if (!entries.length || !this.values || this.dataset.noMatch === 'hide') return this.show();

      this.show('.js-compat-uncertain');
    }

    fits(entry, values) {
      return entry.filters.every(filter => {
        const options = values[this.key(filter.param)] || [];
        if (options.includes(filter.value)) return true;

        /* a taxonomy filter keys on a taxonomy ID the product's own metafields
           never carry, so fall back to the handle behind the filter's label */
        return filter.label ? options.includes(this.handleize(filter.label)) : false;
      });
    }

    handleize(value) {
      return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    /* a filter parameter carries a prefix describing how the filter was built
       (filter.p.m.custom.make, filter.v.t.shopify.flavor, filter.v.option.size,
       filter.p.product_type), so drop that prefix and match on the rest */
    key(param) {
      const parts = param.toLowerCase().split('.');

      if (parts[0] === 'filter') parts.splice(0, 2);
      if (parts[0] === 'm' || parts[0] === 't') parts.shift();

      return parts.join('.');
    }

    show(selector, names) {
      const message = selector ? this.querySelector(selector) : null;

      this.fillNames(names);
      this.messages.forEach(other => { other.hidden = other !== message; });
      this.hidden = message === null;
    }

    fillNames(names) {
      this.nameSlots.forEach(slot => {
        slot.node.nodeValue = slot.template.replace('[name]', names || '[name]');
      });
    }
  }

  customElements.define('compatibility-badge', CompatibilityBadge);
}
